/**
 * Order Billing Panel Component
 * Shows billing information on order detail view.
 * Allows price overrides and additional charges management.
 * 
 * Features:
 * - Display price snapshot from order creation
 * - Show billing status (unbilled, invoiced, paid)
 * - Invoice reference if invoiced
 * - Price override with reason (audit trail)
 * - Additional charges display and management
 * - Total billable amount calculation
 * 
 * Access: Visible only to Accounting and Admin roles
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Edit, 
  FileText, 
  CheckCircle2,
  Clock,
  Receipt,
  AlertTriangle,
  Save,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Info,
  RefreshCw,
  Coins,
  FilePlus2,
  Calculator,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import './order-overview-premium.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatDateTimeLocal, formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Charge type options - includes both manual and system-generated types
const CHARGE_TYPES = [
  { value: 'tax_office_fee', label: 'Tax Office Fee' },
  { value: 'shipment_courier', label: 'Shipment / Courier' },
  { value: 'rush_fee', label: 'Rush Fee' },
  { value: 'recording_fee', label: 'Recording Fee' },
  { value: 'search_fee', label: 'Search Fee' },
  { value: 'other', label: 'Other' },
  // System-generated Mail-Away charges (from Charge Engine)
  { value: 'mailaway_tax_office_fee', label: 'Mail-Away – Tax Office Fee', system: true },
  { value: 'mailaway_shipping', label: 'Mail-Away – Shipping', system: true },
  { value: 'mailaway_sase', label: 'Mail-Away – SASE', system: true },
  // Future system-generated charges
  { value: 'clarification_fee', label: 'Clarification Fee', system: true },
  { value: 'rush_fee_system', label: 'Rush Fee (System)', system: true },
  { value: 'urgent_fee', label: 'Urgent Fee (System)', system: true },
  { value: 'vendor_fee', label: 'Vendor Fee', system: true }
];

/**
 * Order Billing Panel Component
 */
export const OrderBillingPanel = ({ order, userRole, onUpdate, compact = false }) => {
  // Access check - Admin, Accounting, Team Leaders can view
  const hasAccess = ['admin', 'accounting', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'].includes(userRole);
  const canAddCharges = ['admin', 'accounting', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'].includes(userRole);
  const canOverridePrice = ['admin', 'accounting'].includes(userRole);
  
  // State for override editing
  const [isEditing, setIsEditing] = useState(false);
  const [overridePrice, setOverridePrice] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Jul 2026 — app-styled confirmation for Recalculate (replaces window.confirm)
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  
  // State for additional charges
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [newChargeType, setNewChargeType] = useState('');
  const [newChargeAmount, setNewChargeAmount] = useState('');
  const [newChargeDesc, setNewChargeDesc] = useState('');
  const [showChargesExpanded, setShowChargesExpanded] = useState(true);
  // Jul 2026 — in-app confirmation target for charge removal
  const [removeChargeTarget, setRemoveChargeTarget] = useState(null);
  
  // Additional charges from order
  const additionalCharges = order?.additional_charges || [];

  // Mail-Away cycle history (Feb 27, 2026) — derived from
  // order.mail_away_requests, grouped by jurisdiction. Used only for
  // visibility in the billing panel; does not affect totals or any
  // billing calculation. Cycles are sorted by sent_at ascending so
  // Cycle 1, 2, 3… appear top-down.
  const mailAwayCycles = (() => {
    const requests = order?.mail_away_requests || [];
    if (!requests.length) return [];
    const byJur = {};
    for (const r of requests) {
      const key = r.jurisdiction_id || r.jurisdiction_name || 'unknown';
      if (!byJur[key]) {
        byJur[key] = {
          jurisdiction_id: r.jurisdiction_id,
          jurisdiction_name: r.jurisdiction_name || 'Unknown',
          cycles: [],
        };
      }
      byJur[key].cycles.push(r);
    }
    return Object.values(byJur).map((g) => {
      g.cycles.sort((a, b) => {
        const ta = a.sent_at || a.created_at || '';
        const tb = b.sent_at || b.created_at || '';
        return String(ta).localeCompare(String(tb));
      });
      g.totalMlc = g.cycles.reduce((s, c) => s + Number(c.tax_office_fee || 0), 0);
      g.totalShipping = g.cycles.reduce((s, c) => s + Number(c.shipping_fee || 0), 0);
      g.total = g.totalMlc + g.totalShipping;
      return g;
    });
  })();
  const hasMultiCycleJur = mailAwayCycles.some((g) => g.cycles.length > 1);
  // Feb 2026 — Resume audit visibility: show the Cycle History card
  // even for single-cycle jurisdictions if a Resume happened (so the
  // shipment team sees the "Resume after Clarification (No Charge)"
  // informational row).
  const hasAnyResumeAudit = mailAwayCycles.some((g) =>
    g.cycles.some((c) => Array.isArray(c.resume_audit) && c.resume_audit.length > 0)
  );
  // Overall Mail-Away total across ALL jurisdictions on the order
  // (used by the Cycle History footer so the breakdown reconciles
  // against the Additional row even when only one jurisdiction is
  // multi-cycle and the others contribute single-cycle charges).
  const mailAwayGrandTotal = mailAwayCycles.reduce((s, g) => s + g.total, 0);
  
  if (!hasAccess) {
    return null; // Don't show panel to non-authorized users
  }
  
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'Not Set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Map billing_price_resolution.resolution_level → visible source badge.
  // Buckets (May 2, 2026 governance update):
  //   - "client":  Client+Product (with optional state/county) rule matched.
  //   - "product": admin-set product Default Price (Admin → Product Mgmt).
  //                Surfaces ONLY when matched_rule_id is null AND
  //                snapshot > 0 (real admin-controlled value).
  //   - "missing": NO admin rule and NO product default → snapshot == 0.
  //                Order is excluded from billable queue + invoice
  //                generation is hard-blocked. Replaces the prior
  //                "Safety Fallback" $50 baseline.
  // Backward-compat: legacy orders persisted with
  //   resolution_level="safety_fallback" (pre-May 2, 2026) OR
  //   resolution_level="system_default" + snapshot==$50 + no rule_id
  // are still rendered — but as "Missing Pricing" semantically (the
  // billing engine no longer emits either of those today; this is
  // strictly for displaying historical orders sensibly).
  const getPricingSource = () => {
    const res = order?.billing_price_resolution;
    const snapshot = order?.billing_unit_price_snapshot;

    // Highest-priority signal: $0 (or null) snapshot → missing pricing.
    // Catches both new orders (resolution_level=missing_pricing) and
    // any edge case where a snapshot was never written.
    if (snapshot === null || snapshot === undefined || snapshot <= 0) {
      // If admin has set an explicit override, defer to that.
      if ((order?.billing_override_price || 0) > 0) {
        return {
          bucket: 'override',
          label: 'Manual Override',
          description: 'Admin manually set a billing override price for this order.',
          dotClass: 'bg-blue-500',
          badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
        };
      }
      return {
        bucket: 'missing',
        label: 'Missing Pricing',
        description: 'No client pricing rule matched and no product Default Price is configured. Configure pricing in Admin or set a manual override before this order can be invoiced.',
        dotClass: 'bg-red-500',
        badgeClass: 'bg-red-50 text-red-700 border-red-200',
      };
    }

    if (!res || !res.resolution_level) return null;
    const level = String(res.resolution_level).toLowerCase();

    if (['county', 'state', 'client_default'].includes(level)) {
      return {
        bucket: 'client',
        label: 'Client Pricing',
        description: 'Matched a Client + Product pricing rule',
        dotClass: 'bg-emerald-500',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      };
    }

    // Legacy snapshot rendering (will phase out as orders re-snapshot
    // under the new governance model).
    if (level === 'safety_fallback' || (level === 'system_default' && snapshot === 50 && !res.matched_rule_id)) {
      return {
        bucket: 'missing',
        label: 'Missing Pricing',
        description: 'Legacy $50 placeholder — pricing was never properly configured for this order. Configure a Client Pricing rule or a Product Default Price.',
        dotClass: 'bg-red-500',
        badgeClass: 'bg-red-50 text-red-700 border-red-200',
      };
    }

    if (level === 'system_default') {
      return {
        bucket: 'product',
        label: 'Product Default',
        description: 'Using the admin-configured Product Default Price (no client rule matched).',
        dotClass: 'bg-amber-500',
        badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
      };
    }

    if (level === 'missing_pricing') {
      return {
        bucket: 'missing',
        label: 'Missing Pricing',
        description: 'No client pricing rule matched and no product Default Price is configured. Configure pricing in Admin or set a manual override before this order can be invoiced.',
        dotClass: 'bg-red-500',
        badgeClass: 'bg-red-50 text-red-700 border-red-200',
      };
    }
    return null;
  };

  const PricingSourceBadge = ({ size = 'sm' }) => {
    const src = getPricingSource();
    if (!src) return null;
    const sizeClass = size === 'xs' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center gap-1 rounded-full border font-medium ${sizeClass} ${src.badgeClass} cursor-help`}
              data-testid={`pricing-source-badge-${src.bucket}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${src.dotClass}`} />
              {src.label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs">{src.description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateTimeLocal(dateStr);
  };
  
  const getBillingStatus = () => {
    const status = order.billing_status || 'unbilled';
    switch (status) {
      case 'paid':
        return {
          label: 'Paid',
          badge: <Badge className="bg-green-100 text-green-800">Paid</Badge>,
          icon: <CheckCircle2 className="w-5 h-5 text-green-600" />
        };
      case 'invoiced':
        return {
          label: 'Invoiced',
          badge: <Badge className="bg-blue-100 text-blue-800">Invoiced</Badge>,
          icon: <Receipt className="w-5 h-5 text-blue-600" />
        };
      default:
        return {
          label: 'Unbilled',
          badge: <Badge className="bg-yellow-100 text-yellow-800">Unbilled</Badge>,
          icon: <Clock className="w-5 h-5 text-yellow-600" />
        };
    }
  };
  
  const getCurrentPrice = () => {
    if (order.billing_override_price !== null && order.billing_override_price !== undefined) {
      return { amount: order.billing_override_price, isOverride: true };
    }
    if (order.billing_unit_price_snapshot !== null && order.billing_unit_price_snapshot !== undefined) {
      return { amount: order.billing_unit_price_snapshot, isOverride: false };
    }
    return { amount: null, isOverride: false };
  };
  
  // OPS-FEE-TREATMENT-001 — Operational Fees marked "Internal Cost" are PNA
  // expenses: never part of Additional / Total Billable (mirrors backend gate).
  const isClientBillable = (c) => c?.billing_treatment !== 'internal_cost';
  const getAdditionalChargesTotal = () => {
    return additionalCharges.filter(isClientBillable).reduce((sum, c) => sum + (c.amount || 0), 0);
  };
  const internalCostsTotal = additionalCharges
    .filter(c => !isClientBillable(c)).reduce((sum, c) => sum + (c.amount || 0), 0);
  
  const getTotalBillable = () => {
    const basePrice = getCurrentPrice().amount || 0;
    const additionalTotal = getAdditionalChargesTotal();
    return basePrice + additionalTotal;
  };
  
  const handleStartEdit = () => {
    const currentPrice = getCurrentPrice();
    setOverridePrice(currentPrice.amount?.toString() || '');
    setOverrideReason('');
    setIsEditing(true);
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setOverridePrice('');
    setOverrideReason('');
  };
  
  // Recalculate from current pricing rules.
  // Pricing is snapshot-at-creation by design — this button lets Admin/
  // Accounting intentionally refresh the snapshot after client pricing rules
  // are added or updated. Backend: POST /api/pricing/billing/recalculate/{order_id}
  // (routes/pricing.py:1438). Refuses to run if pricing_locked=true.
  // Jul 2026 — confirmation moved from window.confirm() to the in-app
  // modal (showRecalcConfirm); this runs AFTER the user confirms.
  const handleRecalculate = async () => {
    setShowRecalcConfirm(false);
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/pricing/billing/recalculate/${order.id}`);
      const { old_price, new_price, resolution_level, matched_rule_id } = res.data || {};
      if (old_price != null && new_price != null && old_price.toFixed(2) === new_price.toFixed(2)) {
        toast.info('No pricing changes were found. The current billing price is already up to date.', {
          description: `Current price: ${formatCurrency(new_price)} (${resolution_level})`,
        });
      } else {
        toast.success('Billing price recalculated successfully.', {
          description: `${formatCurrency(old_price)} → ${formatCurrency(new_price)} (${resolution_level})`,
        });
      }
      if (!matched_rule_id && resolution_level === 'safety_fallback') {
        toast.warning('Safety fallback ($50) — no pricing configured anywhere. Add a Client Pricing rule or a Product default.');
      } else if (!matched_rule_id && resolution_level === 'system_default') {
        toast.warning('No client pricing rule matched — fell back to system default. Check Admin → Billing → Client Pricing.');
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error recalculating pricing:', error);
      toast.error('Unable to recalculate the billing price. Please try again or contact an administrator.', {
        description: error.response?.data?.detail || undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveOverride = async () => {
    const price = parseFloat(overridePrice);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    if (!overrideReason.trim()) {
      toast.error('Please provide a reason for the price override');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/pricing/billing/override/${order.id}`, {
        override_price: price,
        reason: overrideReason.trim()
      });
      
      toast.success('Billing price updated successfully');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating billing price:', error);
      toast.error(error.response?.data?.detail || 'Failed to update billing price');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddCharge = async () => {
    const amount = parseFloat(newChargeAmount);
    if (!newChargeType) {
      toast.error('Please select a charge type');
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/pricing/orders/${order.id}/charges`, {
        charge_type: newChargeType,
        amount: amount,
        description: newChargeDesc.trim() || null
      });
      
      toast.success('Additional charge added');
      setShowAddCharge(false);
      setNewChargeType('');
      setNewChargeAmount('');
      setNewChargeDesc('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to add charge');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Jul 2026 — in-app confirmation for charge removal (replaces window.confirm)
  const handleRemoveCharge = async () => {
    const chargeId = removeChargeTarget;
    setRemoveChargeTarget(null);
    if (!chargeId) return;

    setIsLoading(true);
    try {
      await axios.delete(`${API}/pricing/orders/${order.id}/charges/${chargeId}`);
      toast.success('Charge removed');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error removing charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove charge');
    } finally {
      setIsLoading(false);
    }
  };
  
  const billingStatus = getBillingStatus();
  const currentPrice = getCurrentPrice();
  const additionalTotal = getAdditionalChargesTotal();
  const totalBillable = getTotalBillable();
  
  // Jul 2026 — in-app Remove Charge confirmation (replaces window.confirm),
  // rendered alongside the recalc modal in both display modes.
  const removeChargeConfirmModal = removeChargeTarget && (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="remove-charge-confirm-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Remove Charge?</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-slate-600">
            This additional charge will be removed from the order's billing.
            This action will be recorded in the audit log.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => setRemoveChargeTarget(null)}
            disabled={isLoading}
            data-testid="cancel-remove-charge-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveCharge}
            disabled={isLoading}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg shadow-red-500/25"
            data-testid="confirm-remove-charge-btn"
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );

  // Jul 2026 — shared in-app Recalculate confirmation (replaces
  // window.confirm), rendered by BOTH compact and full-card modes.
  const recalcConfirmModal = showRecalcConfirm && (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="recalculate-confirm-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Recalculate Price?</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-slate-600">
            This will recalculate the billing price using the current client pricing
            rules and replace the existing billing snapshot. This action will be
            recorded in the audit log.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => setShowRecalcConfirm(false)}
            disabled={isLoading}
            data-testid="cancel-recalculate-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRecalculate}
            disabled={isLoading}
            className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-lg shadow-indigo-500/25"
            data-testid="confirm-recalculate-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
        </div>
      </div>
    </div>
  );

  // Compact mode - return just the content without Card wrapper
  if (compact) {
    return (
      <div className="billing-panel-compact space-y-2.5" data-testid="order-billing-panel">
        {/* Billing Note for Cancelled with Mail-Away */}
        {order.billing_note && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
              <p className="text-orange-900">{order.billing_note}</p>
            </div>
          </div>
        )}
        
        {/* Price Summary — TITLEFLOW-ORDER-OVERVIEW-UI-001 premium tiles (values/logic unchanged) */}
        <div className="ov-tiles" data-testid="billing-price-tiles">
          <div className="ov-tile" style={{ '--t-bg': '#eff6ff', '--ic-c': '#2563eb', '--v-c': '#0f172a' }} data-testid="billing-tile-base">
            <span className="ov-tile-ic"><Coins /></span>
            <div className="min-w-0">
              <p className="ov-tile-label">Base Price
                {canOverridePrice && order.billing_price_resolution && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild><Info className="!w-3 !h-3 text-slate-400 cursor-help" /></TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <div className="text-xs space-y-1">
                          <p className="font-medium">Pricing Details</p>
                          <p>Resolution: {order.billing_price_resolution.resolution_level}</p>
                          {order.billing_price_resolution.matched_rule_id && <p>Rule: {order.billing_price_resolution.matched_rule_id.slice(0, 12)}...</p>}
                          {order.billing_price_resolution.state && <p>State: {order.billing_price_resolution.state}</p>}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </p>
              <p className="ov-tile-value">{formatCurrency(currentPrice.amount)}{currentPrice.isOverride && <small>(Override)</small>}</p>
              <div className="mt-1"><PricingSourceBadge size="xs" /></div>
            </div>
          </div>
          <div className="ov-tile" style={{ '--t-bg': '#ecfdf5', '--ic-c': '#059669', '--v-c': '#0f172a' }} data-testid="billing-tile-additional">
            <span className="ov-tile-ic"><FilePlus2 /></span>
            <div className="min-w-0">
              <p className="ov-tile-label">Additional</p>
              <p className="ov-tile-value">{formatCurrency(additionalTotal)}</p>
              {internalCostsTotal > 0 && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid="billing-internal-costs-note">+ {formatCurrency(internalCostsTotal)} internal cost (not billed)</p>
              )}
            </div>
          </div>
          {canOverridePrice && (
            <div className="ov-tile" style={{ '--t-bg': '#fff7ed', '--ic-c': '#ea580c', '--v-c': '#0f172a' }} data-testid="billing-tile-internal-cost">
              <span className="ov-tile-ic"><Calculator /></span>
              <div className="min-w-0">
                <p className="ov-tile-label">Internal Cost</p>
                {order.internal_override_price != null
                  ? <p className="ov-tile-value">{formatCurrency(order.internal_override_price)}<small>(Override)</small></p>
                  : order.internal_unit_price_snapshot != null
                  ? <p className="ov-tile-value">{formatCurrency(order.internal_unit_price_snapshot)}</p>
                  : <p className="ov-tile-value is-muted">Not Set</p>}
              </div>
            </div>
          )}
          <div className="ov-tile" style={{ '--t-bg': '#f5f3ff', '--ic-c': '#7c3aed', '--v-c': '#059669' }} data-testid="billing-tile-total">
            <span className="ov-tile-ic"><BarChart3 /></span>
            <div className="min-w-0">
              <p className="ov-tile-label">Total Billable</p>
              <p className="ov-tile-value" data-testid="billing-total-billable">{formatCurrency(totalBillable)}</p>
            </div>
          </div>
        </div>

        {/* Margin + actions row (Admin only — same gates as before) */}
        {canOverridePrice && (
          <div className="ov-margin-row">
            <div className="ov-margin" data-testid="billing-margin">
              <span className="ov-tile-ic"><TrendingUp /></span>
              <div>
                <p className="ov-tile-label">Margin</p>
                {(() => {
                  const extPrice = currentPrice.amount || 0;
                  const intCost = order.internal_override_price ?? order.internal_unit_price_snapshot ?? 0;
                  if (extPrice > 0 && intCost > 0) {
                    const margin = ((extPrice - intCost) / extPrice) * 100;
                    return <span className={`ov-margin-value ${margin > 40 ? 'text-emerald-600' : margin >= 20 ? 'text-amber-600' : 'text-red-600'}`}>{margin.toFixed(1)}%</span>;
                  }
                  return <span className="ov-margin-value text-slate-400">—</span>;
                })()}
              </div>
            </div>
            {order.billing_status !== 'paid' && (
              <div className="flex items-center gap-2">
                <button type="button" className="ov-btn" onClick={handleStartEdit} data-testid="edit-billing-btn">
                  <Edit /> {currentPrice.isOverride ? 'Edit Override' : 'Override'}
                </button>
                <button type="button" className="ov-btn" onClick={() => setShowRecalcConfirm(true)} disabled={isLoading || order.pricing_locked}
                  title={order.pricing_locked ? 'Pricing is locked for this order — completed orders cannot be recalculated.' : 'Re-resolve price using current client pricing rules. Audit-logged.'}
                  data-testid="recalculate-billing-btn">
                  <RefreshCw className={isLoading ? 'animate-spin' : ''} /> Recalculate
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Add Charge Button - For authorized users */}
        {canAddCharges && !canOverridePrice && order.billing_status !== 'paid' && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddCharge(!showAddCharge)}
              className="text-xs h-7 bg-white border-slate-300 hover:bg-slate-50"
              data-testid="add-charge-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Charge
            </Button>
          </div>
        )}
        
        {/* Add Charge Form - Compact */}
        {showAddCharge && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2 shadow-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newChargeType} onValueChange={setNewChargeType}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CHARGE_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value} className="text-xs">
                        {ct.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newChargeAmount}
                    onChange={(e) => setNewChargeAmount(e.target.value)}
                    className="pl-5 h-7 text-xs"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddCharge(false)} className="flex-1 h-6 text-xs">
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddCharge} disabled={isLoading} className="flex-1 h-6 text-xs">
                {isLoading ? 'Adding...' : 'Add Charge'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Override Form - Compact */}
        {isEditing && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2 shadow-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">New Price (USD)</Label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={overridePrice}
                    onChange={(e) => setOverridePrice(e.target.value)}
                    className="pl-5 h-7 text-xs"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="h-7 text-xs"
                  placeholder="Reason for override..."
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelEdit} className="flex-1 h-6 text-xs">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveOverride} disabled={isLoading} className="flex-1 h-6 text-xs">
                {isLoading ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </div>
        )}

        {/* Mail-Away Cycle History (Feb 27, 2026) — also visible in
            compact mode so admins reviewing the Overview tab can see
            WHY there are doubled MLC + Shipping charges. Renders only
            when at least one jurisdiction has 2+ cycles. Purely
            informational; does not affect totals. */}
        {hasMultiCycleJur || hasAnyResumeAudit ? (
          <div
            className="pt-2 border-t border-slate-200"
            data-testid="mailaway-cycle-history"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                Mail-Away Cycle History
              </p>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                informational
              </span>
            </div>
            <div className="space-y-2">
              {mailAwayCycles.map((g) => (
                  <div key={g.jurisdiction_id || g.jurisdiction_name} className="text-xs">
                    <div className="font-medium text-slate-800 mb-0.5">{g.jurisdiction_name}</div>
                    <div className="space-y-0.5 pl-2 border-l-2 border-purple-200">
                      {g.cycles.map((c, idx) => {
                        const mlc = Number(c.tax_office_fee || 0);
                        const ship = Number(c.shipping_fee || 0);
                        const cycleN = c.cycle_index || idx + 1;
                        const triggerLabel = {
                          initial: 'initial',
                          post_clarification: 'post-clarification',
                          post_response: 'post-response',
                          manual_resend: 'manual resend',
                        }[c.trigger] || '';
                        const resumeAudit = Array.isArray(c.resume_audit) ? c.resume_audit : [];
                        return (
                          <React.Fragment key={c.request_id}>
                            <div
                              className="flex justify-between text-slate-600 py-0.5 pl-2"
                              data-testid={`mailaway-cycle-history-row-${c.request_id}`}
                            >
                              <span>
                                Cycle {cycleN}
                                {triggerLabel && cycleN > 1 && (
                                  <span className="text-slate-400"> · {triggerLabel}</span>
                                )}
                                <span className="text-slate-400"> · MLC ${mlc.toFixed(2)} + Shipping ${ship.toFixed(2)}</span>
                              </span>
                              <span className="font-semibold text-slate-700">${(mlc + ship).toFixed(2)}</span>
                            </div>
                            {/* Feb 2026 — Resume audit informational rows.
                                Non-billable, do NOT add to subtotal/grand
                                total. Surfaced so handlers see "Resume
                                after Clarification (No Charge)" inline. */}
                            {resumeAudit.map((ra, raIdx) => (
                              <div
                                key={`${c.request_id}-resume-${raIdx}`}
                                className="flex justify-between text-emerald-700 py-0.5 pl-4 italic"
                                data-testid={`mailaway-resume-audit-${c.request_id}-${raIdx}`}
                              >
                                <span>
                                  ↳ Resume after Clarification
                                  <span className="text-emerald-500 not-italic"> (No Charge)</span>
                                  {ra.at && (
                                    <span className="text-emerald-400 not-italic">
                                      {' · '}{formatDateOnly(ra.at)}
                                    </span>
                                  )}
                                </span>
                                <span className="text-emerald-600 font-medium">$0.00</span>
                              </div>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Per-jurisdiction subtotal — shown when that
                          jurisdiction has 2+ cycles. Single-cycle
                          jurisdictions don't need a subtotal because
                          their one row already states the total. */}
                      {g.cycles.length > 1 && (
                        <div className="flex justify-between font-semibold text-purple-800 pt-1 mt-1 border-t border-purple-100 pl-2">
                          <span>Subtotal ({g.cycles.length} cycles)</span>
                          <span>${g.total.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              {/* Mail-Away grand total — reconciles against the
                  Additional row above. */}
              <div
                className="flex justify-between font-semibold text-purple-900 pt-2 mt-1 border-t border-purple-200"
                data-testid="mailaway-cycle-history-grand-total"
              >
                <span>Mail-Away Total ({mailAwayCycles.length} jurisdiction{mailAwayCycles.length !== 1 ? 's' : ''})</span>
                <span>${mailAwayGrandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : null}
        {recalcConfirmModal}
        {removeChargeConfirmModal}
      </div>
    );
  }
  
  // Full Card mode
  return (
    <Card className="billing-panel" data-testid="order-billing-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <DollarSign className="w-5 h-5 text-green-600" />
          Billing Information
          <span className="ml-auto">{billingStatus.badge}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Billing Note for Cancelled with Mail-Away */}
        {order.billing_note && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-amber-800">{order.billing_note}</p>
            </div>
          </div>
        )}
        
        {/* Price Summary */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-600">Base Price</span>
              {/* Admin-only tooltip for pricing details */}
              {canOverridePrice && order.billing_price_resolution && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="text-xs space-y-1">
                        <p className="font-medium">Pricing Details (Admin)</p>
                        <p>Resolution: {order.billing_price_resolution.resolution_level}</p>
                        {order.billing_price_resolution.matched_rule_id && (
                          <p>Rule ID: {order.billing_price_resolution.matched_rule_id.slice(0, 12)}...</p>
                        )}
                        {order.billing_price_resolution.state && (
                          <p>State: {order.billing_price_resolution.state}</p>
                        )}
                        {order.billing_price_resolution.product_id_used && (
                          <p>Product ID: {order.billing_price_resolution.product_id_used.slice(0, 12)}...</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="text-right">
              <span className="font-medium">{formatCurrency(currentPrice.amount)}</span>
              {currentPrice.isOverride && (
                <Badge variant="outline" className="ml-2 text-xs">Override</Badge>
              )}
              <div className="mt-1 flex justify-end">
                <PricingSourceBadge size="sm" />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Additional Charges</span>
            <span className="font-medium">{formatCurrency(additionalTotal)}</span>
          </div>
          
          <div className="border-t pt-2 flex justify-between items-center">
            <span className="font-semibold text-gray-900">Total Billable</span>
            <span className="font-bold text-lg text-green-600">{formatCurrency(totalBillable)}</span>
          </div>
        </div>
        
        {/* Mail-Away Cycle History (Feb 27, 2026) — purely informational.
            Helps billing/audit understand WHY there are multiple MLC +
            Shipping charges for the same jurisdiction. Only renders
            when at least one jurisdiction has 2+ cycles. */}
        {hasMultiCycleJur || hasAnyResumeAudit ? (
          <div className="border rounded-lg" data-testid="mailaway-cycle-history">
            <div className="px-4 py-2 text-sm font-medium text-gray-700 bg-purple-50/40 border-b border-purple-100 flex items-center gap-2">
              <span>Mail-Away Cycle History</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-normal">
                informational
              </span>
            </div>
            <div className="px-4 py-3 space-y-3">
              {mailAwayCycles.map((g) => (
                  <div key={g.jurisdiction_id || g.jurisdiction_name} className="text-sm">
                    <div className="font-medium text-gray-900 mb-1">{g.jurisdiction_name}</div>
                    <div className="space-y-0.5 pl-2 border-l-2 border-purple-200">
                      {g.cycles.map((c, idx) => {
                        const mlc = Number(c.tax_office_fee || 0);
                        const ship = Number(c.shipping_fee || 0);
                        const cycleN = c.cycle_index || idx + 1;
                        const triggerLabel = {
                          initial: 'initial',
                          post_clarification: 'post-clarification',
                          post_response: 'post-response',
                          manual_resend: 'manual resend',
                        }[c.trigger] || '';
                        const resumeAudit = Array.isArray(c.resume_audit) ? c.resume_audit : [];
                        return (
                          <React.Fragment key={c.request_id}>
                            <div
                              className="flex justify-between text-xs text-gray-600 py-0.5 pl-2"
                              data-testid={`mailaway-cycle-history-row-${c.request_id}`}
                            >
                              <span>
                                Cycle {cycleN}
                                {triggerLabel && cycleN > 1 && (
                                  <span className="text-gray-400"> · {triggerLabel}</span>
                                )}
                                <span className="text-gray-400"> · MLC ${mlc.toFixed(2)} + Shipping ${ship.toFixed(2)}</span>
                              </span>
                              <span className="font-medium text-gray-700">${(mlc + ship).toFixed(2)}</span>
                            </div>
                            {resumeAudit.map((ra, raIdx) => (
                              <div
                                key={`${c.request_id}-resume-${raIdx}`}
                                className="flex justify-between text-xs text-emerald-700 py-0.5 pl-4 italic"
                                data-testid={`mailaway-resume-audit-${c.request_id}-${raIdx}`}
                              >
                                <span>
                                  ↳ Resume after Clarification
                                  <span className="text-emerald-500 not-italic"> (No Charge)</span>
                                  {ra.at && (
                                    <span className="text-emerald-400 not-italic">
                                      {' · '}{formatDateOnly(ra.at)}
                                    </span>
                                  )}
                                </span>
                                <span className="text-emerald-600 font-medium">$0.00</span>
                              </div>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {g.cycles.length > 1 && (
                        <div className="flex justify-between text-xs font-semibold text-purple-800 pt-1 mt-1 border-t border-purple-100 pl-2">
                          <span>Subtotal ({g.cycles.length} cycles)</span>
                          <span>${g.total.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              {/* Mail-Away grand total — reconciles against the
                  Additional row above so admins can verify the
                  Cycle History adds up to the billed Additional. */}
              <div
                className="flex justify-between text-sm font-semibold text-purple-900 pt-2 mt-1 border-t border-purple-200"
                data-testid="mailaway-cycle-history-grand-total"
              >
                <span>Mail-Away Total ({mailAwayCycles.length} jurisdiction{mailAwayCycles.length !== 1 ? 's' : ''})</span>
                <span>${mailAwayGrandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Additional Charges Section */}
        {(additionalCharges.length > 0 || canAddCharges) && (
          <div className="border rounded-lg">
            <button
              onClick={() => setShowChargesExpanded(!showChargesExpanded)}
              className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>Additional Charges ({additionalCharges.length})</span>
              {showChargesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showChargesExpanded && (
              <div className="border-t px-4 py-3 space-y-2">
                {additionalCharges.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">No additional charges</p>
                ) : (
                  additionalCharges.map((charge) => {
                    const chargeType = CHARGE_TYPES.find(t => t.value === charge.charge_type);
                    const isSystemGenerated = charge.is_system_generated || chargeType?.system;
                    const isLocked = charge.is_locked;
                    
                    return (
                      <div 
                        key={charge.id} 
                        className={`flex items-center justify-between text-sm rounded p-2 ${
                          isSystemGenerated ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {charge.label || chargeType?.label || charge.charge_type}
                            </span>
                            {isSystemGenerated && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-200">
                                Auto
                              </Badge>
                            )}
                            {!isClientBillable(charge) && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-700 border-slate-200" data-testid={`billing-charge-internal-${charge.id}`}>
                                Internal Cost · not billed
                              </Badge>
                            )}
                            {isLocked && (
                              <span className="text-gray-400" title="System-generated charge (locked)">
                                🔒
                              </span>
                            )}
                          </div>
                          {charge.description && (
                            <p className="text-xs text-gray-500 truncate">{charge.description}</p>
                          )}
                          <p className="text-xs text-gray-400">
                            {isSystemGenerated ? 'System' : `Added by ${charge.added_by_name || charge.created_by_name}`} on {formatDate(charge.added_at || charge.created_at).split(',')[0]}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className={`font-medium ${isSystemGenerated ? 'text-blue-600' : 'text-amber-600'}`}>
                            {formatCurrency(charge.amount)}
                          </span>
                          {canAddCharges && order.billing_status !== 'paid' && !isLocked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRemoveChargeTarget(charge.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                
                {/* Add Charge Form */}
                {canAddCharges && order.billing_status !== 'paid' && (
                  <>
                    {!showAddCharge ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddCharge(true)}
                        className="w-full mt-2"
                        data-testid="add-charge-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Charge
                      </Button>
                    ) : (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Type *</Label>
                            <Select value={newChargeType} onValueChange={setNewChargeType}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {CHARGE_TYPES.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Amount *</Label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={newChargeAmount}
                                onChange={(e) => setNewChargeAmount(e.target.value)}
                                className="h-8 pl-5 text-xs"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={newChargeDesc}
                            onChange={(e) => setNewChargeDesc(e.target.value)}
                            className="h-8 text-xs"
                            placeholder="Optional description..."
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowAddCharge(false);
                              setNewChargeType('');
                              setNewChargeAmount('');
                              setNewChargeDesc('');
                            }}
                            className="flex-1 h-7 text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleAddCharge}
                            disabled={isLoading}
                            className="flex-1 h-7 text-xs"
                            data-testid="save-charge-btn"
                          >
                            {isLoading ? 'Adding...' : 'Add'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Override Info */}
        {order.billing_override_price !== null && order.billing_override_price !== undefined && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Price Override Applied</p>
                <p className="text-yellow-700 mt-1">
                  Reason: {order.billing_override_reason || 'Not specified'}
                </p>
                {order.billing_override_by_name && (
                  <p className="text-yellow-600 text-xs mt-1">
                    By: {order.billing_override_by_name} on {formatDate(order.billing_override_at)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Invoice Reference */}
        {order.invoice_number && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Invoice: {order.invoice_number}</p>
                {order.invoiced_at && (
                  <p className="text-blue-600 text-xs">Invoiced on: {formatDate(order.invoiced_at)}</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Recalculate + Override Buttons */}
        {canOverridePrice && (
          <>
            {!isEditing ? (
              <div className="flex gap-2">
                {/* Override Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  className="flex-1"
                  disabled={order.billing_status === 'paid'}
                  data-testid="edit-billing-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {currentPrice.isOverride ? 'Edit Override' : 'Override Price'}
                </Button>
                {/* Recalculate Button — re-resolve from current rules */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRecalcConfirm(true)}
                  className="flex-1"
                  disabled={isLoading || order.billing_status === 'paid' || order.pricing_locked}
                  title={order.pricing_locked
                    ? 'Pricing is locked for this order — completed orders cannot be recalculated.'
                    : 'Re-resolve price using current client pricing rules. Audit-logged.'}
                  data-testid="recalculate-billing-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Recalculate from current rules
                </Button>
              </div>
            ) : (
              <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                <div>
                  <Label>New Price (USD)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={overridePrice}
                      onChange={(e) => setOverridePrice(e.target.value)}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="override-price-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Reason for Override *</Label>
                  <Textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Enter reason for price change..."
                    rows={2}
                    data-testid="override-reason-input"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancelEdit} className="flex-1">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveOverride} disabled={isLoading} className="flex-1" data-testid="save-override-btn">
                    <Save className="w-4 h-4 mr-1" /> {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        
        {order.billing_status === 'paid' && (
          <p className="text-xs text-gray-500 text-center">
            Billing cannot be modified after payment
          </p>
        )}

        {/* Jul 2026 — in-app Recalculate confirmation (replaces window.confirm) */}
        {recalcConfirmModal}
        {removeChargeConfirmModal}
      </CardContent>
    </Card>
  );
};

export default OrderBillingPanel;
